import { IconAlertTriangle, IconLock, IconMail } from '@tabler/icons-react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'

import { useSession } from '../auth/useSession'
import { ApiError, apiRequest, safeNextPath } from '../lib/apiClient'
import type { SessionResponse } from '../lib/apiTypes'
import { formatDateTimeWib, formatCountdown } from '../lib/format'

type LoginProblem =
  | { kind: 'none' }
  | { kind: 'invalid' }
  | { kind: 'totp_required' | 'totp_invalid' }
  | { kind: 'locked'; until: string | null }
  | { kind: 'rate_limited'; retryAfter: number }
  | { kind: 'other'; message: string; status: number }

/**
 * Halaman masuk. Pesan dibedakan menurut kode error kontrak: 401 pesan sama
 * untuk email salah dan pengguna nonaktif, 423 menyebut waktu buka kunci, dan
 * 429 menghitung mundur dari header Retry-After.
 */
export function LoginPage() {
  const navigate = useNavigate()
  const { signIn } = useSession()
  const [params] = useSearchParams()
  const next = safeNextPath(params.get('next'))

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [totpCode, setTotpCode] = useState('')
  const [totpChallenge, setTotpChallenge] = useState(false)
  const totpRef = useRef<HTMLInputElement>(null)
  const [problem, setProblem] = useState<LoginProblem>({ kind: 'none' })
  const [submitting, setSubmitting] = useState(false)
  const [retryAfter, setRetryAfter] = useState(0)
  const emailRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    emailRef.current?.focus()
  }, [])

  // Hitung mundur hanya berjalan selama pesan 429 tampil. Nilai awalnya diisi
  // saat error diterima, bukan di dalam effect, supaya tidak ada render berantai.
  useEffect(() => {
    if (problem.kind !== 'rate_limited' || retryAfter <= 0) {
      return
    }
    const timer = window.setInterval(() => {
      setRetryAfter((value) => (value <= 1 ? 0 : value - 1))
    }, 1000)
    return () => {
      window.clearInterval(timer)
    }
  }, [problem.kind, retryAfter])

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setSubmitting(true)
    setProblem({ kind: 'none' })
    try {
      const session = await apiRequest<SessionResponse>('/auth/login', {
        method: 'POST',
        body: { email, password, ...(totpChallenge ? { totp_code: totpCode } : {}) },
      })
      signIn(session)
      setPassword('')
      setTotpCode('')
      navigate(next, { replace: true })
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.code === 'totp_required' || error.code === 'totp_invalid') {
          setTotpChallenge(true)
          setTotpCode('')
          setProblem({ kind: error.code })
          window.setTimeout(() => totpRef.current?.focus(), 0)
          return
        }
        if (error.code === 'account_locked' || error.status === 423) {
          setProblem({ kind: 'locked', until: error.lockedUntil })
        } else if (error.code === 'rate_limited' || error.status === 429) {
          const seconds = readRetryAfter(error)
          setRetryAfter(seconds)
          setProblem({ kind: 'rate_limited', retryAfter: seconds })
        } else if (error.status === 401) {
          setProblem({ kind: 'invalid' })
        } else {
          setProblem({ kind: 'other', message: error.message, status: error.status })
        }
      } else {
        setProblem({
          kind: 'other',
          message: 'Tidak bisa menghubungi server admin.',
          status: 0,
        })
      }
      setPassword('')
    } finally {
      setSubmitting(false)
    }
  }

  const blocked = problem.kind === 'rate_limited' && retryAfter > 0

  return (
    <div className="login">
      <div className="login__card">
        <div className="login__brand">
          <span className="login__logo-slot" aria-hidden="true">
            TP
          </span>
          <span>
            <span className="login__wordmark">TAHU Pay</span>
            <br />
            <span className="login__owner">Pemerintah Kabupaten Sumedang</span>
          </span>
        </div>

        <h1>Masuk ke konsol</h1>
        <p className="muted">
          Konsol ini untuk operator pembayaran daerah, baik QRIS maupun Virtual Account.
          Masuk memakai email dan password yang diberikan administrator.
        </p>

        <form className="login__form" onSubmit={(event) => void onSubmit(event)} noValidate>
          <div className="field">
            <label className="field__label" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              ref={emailRef}
              className="input"
              type="email"
              name="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => { setEmail(event.target.value); if (totpChallenge) { setPassword(''); setTotpCode(''); setTotpChallenge(false); setProblem({ kind: 'none' }) } }}
              aria-invalid={problem.kind === 'invalid' || undefined}
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="password">
              Password
            </label>
            <input
              id="password"
              className="input"
              type="password"
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              aria-invalid={problem.kind === 'invalid' || undefined}
            />
          </div>

          {totpChallenge ? (
            <div className="field">
              <label className="field__label" htmlFor="totp-code">Kode autentikator</label>
              <input id="totp-code" ref={totpRef} className="input" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={totpCode} onChange={(event) => setTotpCode(event.target.value.replace(/\D/g, ''))} aria-invalid={problem.kind === 'totp_invalid'} />
              <button type="button" className="btn" onClick={() => { setTotpChallenge(false); setTotpCode(''); setPassword(''); setProblem({ kind: 'none' }) }}>Batalkan verifikasi</button>
            </div>
          ) : null}
          <LoginProblemMessage problem={problem} retryAfter={retryAfter} />

          <button
            type="submit"
            className="btn btn--primary"
            disabled={submitting || blocked || email === '' || password === '' || (totpChallenge && totpCode.length !== 6)}
          >
            <IconLock size={18} stroke={1.75} aria-hidden="true" />
            {submitting ? 'Memeriksa...' : 'Masuk'}
          </button>
        </form>

        <p className="login__foot">
          Sesi berakhir setelah 30 menit tanpa aktivitas, dan paling lama 12 jam sejak
          masuk.
        </p>
      </div>
    </div>
  )
}

function readRetryAfter(error: ApiError): number {
  if (error.retryAfter !== null) {
    return error.retryAfter
  }
  const fromCode = /coba lagi dalam (?:(\d+)m)?([\d.]+)s/.exec(error.message)
  if (fromCode) {
    return Number(fromCode[1] ?? 0) * 60 + Math.ceil(Number(fromCode[2]))
  }
  return 60
}

function LoginProblemMessage({
  problem,
  retryAfter,
}: {
  problem: LoginProblem
  retryAfter: number
}) {
  if (problem.kind === 'none') {
    return null
  }
  if (problem.kind === 'totp_required' || problem.kind === 'totp_invalid') {
    return <div className="alert alert--warning" role="alert">{problem.kind === 'totp_required' ? 'Masukkan kode enam digit dari aplikasi autentikator.' : 'Kode autentikator tidak berlaku atau sudah dipakai. Tunggu kode berikutnya, lalu coba lagi.'}</div>
  }
  if (problem.kind === 'invalid') {
    return (
      <div className="alert alert--danger" role="alert">
        <span className="alert__icon">
          <IconAlertTriangle size={18} stroke={1.75} aria-hidden="true" />
        </span>
        <div>
          <p>Email atau password salah, atau akun ini dinonaktifkan.</p>
          <p className="muted">
            Periksa ejaan email, lalu coba lagi. Bila akun baru dinonaktifkan,
            hubungi administrator untuk mengaktifkannya kembali.
          </p>
        </div>
      </div>
    )
  }
  if (problem.kind === 'locked') {
    return (
      <div className="alert alert--warning" role="alert">
        <span className="alert__icon">
          <IconAlertTriangle size={18} stroke={1.75} aria-hidden="true" />
        </span>
        <div>
          <p>
            Akun terkunci karena percobaan masuk yang gagal berulang.
            {problem.until ? ` Terbuka kembali pada ${formatDateTimeWib(problem.until)}.` : ''}
          </p>
          <p className="muted">
            Tunggu sampai waktu itu, atau minta administrator membuka kuncinya dari
            sisi server.
          </p>
        </div>
      </div>
    )
  }
  if (problem.kind === 'rate_limited') {
    return (
      <div className="alert alert--warning" role="alert">
        <span className="alert__icon">
          <IconAlertTriangle size={18} stroke={1.75} aria-hidden="true" />
        </span>
        <div>
          <p>
            Terlalu banyak percobaan masuk dari alamat ini.
            {retryAfter > 0
              ? ` Coba lagi dalam ${formatCountdown(retryAfter)}.`
              : ' Kamu bisa mencoba lagi sekarang.'}
          </p>
          <p className="muted">
            Batas ini dihitung per alamat email dan alamat jaringan, jadi menunggu
            sebentar cukup.
          </p>
        </div>
      </div>
    )
  }
  if (problem.kind === 'other') {
    return (
      <div className="alert alert--danger" role="alert">
        <span className="alert__icon">
          <IconMail size={18} stroke={1.75} aria-hidden="true" />
        </span>
        <div>
          <p>{problem.message}</p>
          {problem.status > 0 ? <p className="muted">Kode HTTP {problem.status}.</p> : null}
        </div>
      </div>
    )
  }
  return null
}
